import {
  post as apiPost,
  put as apiPut,
  TYPE_BROOKLYN,
  TYPE_BROOKLYN_JSON,
  get as apiGet,
} from 'api-client'
import {
  format as formatDateTime,
  getDateTime,
  getCurrentTime,
  getDifferenceInDays,
  formatDateWithTimezonOffset,
  getDateTimeUTC,
} from 'services/date-time'
import _get from 'lodash/get'
import _startsWith from 'lodash/startsWith'
import _includes from 'lodash/includes'
import _assign from 'lodash/assign'
import { List, Map, fromJS } from 'immutable'
import _cloneDeep from 'lodash/cloneDeep'
import { isEntitled, filterStandardSubscriptions } from 'services/subscription'
import { selectMostRecentEligibleEntitlementFromUser } from 'services/user/selectors'
import {
  PLAN_ID_ONE_DOLLAR_THIRTY_DAYS_TO_MONTHLY,
  PLAN_ID_ONE_MONTH_TRIAL_TO_ANNUAL,
  PLAN_ID_COMPLIMENTARY,
  PLAN_ID_MONTHLY,
  PLAN_ID_ANNUAL,
  PLAN_SUBSCRIPTION_CANCELLED,
  PLAN_SUBSCRIPTION_ANNUAL,
  PLAN_CATEGORY_AI_ONLY,
  PLAN_SKU_SAGE_SPECIAL_ACCESS,
  PLAN_SKU_INCREASED_PRICE_ANNUAL,
  PLAN_SKU_INCREASED_PRICE_MONTHLY,
  PLAN_SKU_THREE_MONTH_TO_ANNUAL_SPECIAL,
  PLAN_SKU_THREE_MONTH_TO_MONTHLY_SPECIAL,
  PLAN_SKU_PRICE_INCREASE_2026_ANNUAL,
  PLAN_SKU_THREE_MONTH_WITH_TRIAL,
  PLAN_SKU_PRICE_INCREASE_2026_ANNUAL_SPECIAL,
  PLAN_SKU_THREE_MONTH_WITH_TRIAL_CL,
  PLAN_CATEGORY_MONTHLY,
  PLAN_CATEGORY_ANNUAL,
  PLAN_CATEGORY_GAIA_PLUS,
  PRIMARY_PLAN_SKUS,
  getPlanSubscriptionType,
  PLAN_SKU_COMPLIMENTARY,
} from 'services/plans'
import {
  PAYMENT_PROVIDER_NAME_GCSI,
  PAYMENT_PROVIDER_NAME_ZUORA,
} from 'services/paytrack'
import { FORM_SUBMISSION_NAME_CANCEL_FLOW } from 'services/form-submissions'
import { get as getConfig } from 'config'
import _parseInt from 'lodash/parseInt'
import {
  TAXABLE_COUNTRIES,
  US,
  DE,
  AT,
  ME,
  XK,
} from 'services/country'
import { getAuthIsLoggedIn } from 'services/auth'
import { useSelector } from 'react-redux'
// eslint-disable-next-line import/no-self-import, import/no-cycle
import { selectHasGiftSubscription } from 'services/user-account/selectors'

const config = getConfig()
const unifydToggles = _get(config, ['features', 'userAccount', 'unifyd'])
const isUnifydMigrationStepOne = !!unifydToggles.migrationStepOne
const isUnifydMigrationStepTwo = !!unifydToggles.migrationStepTwo
const isCancelThreeMonthOfferEnabled = !!_get(config, ['features', 'userAccount', 'cancelThreeMonthOffer'])
const cancelPriceIncreaseOfferToggles = _get(config, ['features', 'userAccount', 'cancelPriceIncreaseOffer'], {})
const isCancelPriceIncreaseOfferEnabled = (subscriptionType) => {
  const toggles = cancelPriceIncreaseOfferToggles
  switch (subscriptionType) {
    case PLAN_CATEGORY_MONTHLY:
      return !!toggles.monthly
    case PLAN_CATEGORY_ANNUAL:
      return !!toggles.annual
    case PLAN_CATEGORY_GAIA_PLUS:
      return !!toggles.gaiaPlus
    default:
      return false
  }
}

/**
 * Determines if Cancel Flow 1F should be shown.
 * Requires: feature flag enabled AND current plan category is annual.
 * @param {Object} props - { userAccount } from Redux state
 * @returns {Boolean}
 */
export function shouldShowCancelFlow1f (props) {
  const { userAccount } = props
  const cancelFlow1fEnabled = !!_get(getConfig(), ['features', 'userAccount', 'cancelFlow1f'])
  if (!cancelFlow1fEnabled) return false

  const planCategory = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'planCategory']) || ''
  return planCategory === PLAN_CATEGORY_ANNUAL
}

export const SPECIAL_OFFER_DURATION = 30
export const SPECIAL_OFFER_DURATION_MONTHS = 1

export const USER_ACCOUNT_PENDING = 'Pending'
export const USER_ACCOUNT_ACTIVE = 'Active'
export const USER_ACCOUNT_PAUSED = 'Paused'
export const USER_ACCOUNT_CANCELLED = 'Cancelled'
export const USER_ACCOUNT_ANONYMOUS = 'Anonymous'
export const USER_ACCOUNT_UNENTITLED = 'Unentitled'
export const USER_ACCOUNT_RECURRING_PLAN = 'Recurring'
export const USER_ACCOUNT_TRANSACTION_STATUS_DECLINED = 'Declined'
export const USER_ACCOUNT_TRANSACTION_STATUS_APPROVED = 'Approved'

export const PAUSE_REASON = 'pause_reason'

export const TEXTBOX_CANCEL_REASON_OTHER_ANSWER = 'cancel_reason_other_answer'

export const ACCOUNT_TYPE_DEFAULT = 'default'
export const ACCOUNT_TYPE_ITUNES = 'itunes'
export const ACCOUNT_TYPE_GOOGLE = 'google'
export const ACCOUNT_TYPE_ROKU = 'roku'
export const ACCOUNT_TYPE_AMAZON = 'amazon'
export const ACCOUNT_TYPE_GCSI = 'gcsi'
export const ACCOUNT_TYPE_ZUORA = 'zuora'
export const ACCOUNT_TYPE_AI_ONLY = 'zuoraAIOnly'
export const ACCOUNT_TYPE_SECONDARY_PROFILE = 'secondary'
export const ACCOUNT_TYPE_TRIAL = 'trial'
export const ACCOUNT_TYPE_ANDROID_PAY = 'android_pay'
export const ACCOUNT_TYPE_PAYPAL_RECURRING = 'paypal_recurring'

export const ACCOUNT_TYPE_ZUORA_GIFT = 'zuora_gift'
export const ACCOUNT_TYPE_FMTV = 'fmtv'
export const ACCOUNT_TYPE_FMTV_ROKU = 'fmtv_roku'
export const ACCOUNT_TYPE_FMTV_ITUNES = 'fmtv_itunes'
export const ACCOUNT_TYPE_FMTV_PAYPAL = 'fmtv_paypal'
export const ACCOUNT_TYPE_FMTV_ANDROID = 'fmtv_android'
export const ACCOUNT_TYPE_FMTV_GIFT = 'fmtv_gift'
export const ACCOUNT_TYPE_FMTV_COMP = 'fmtv_comp'

export const ACCOUNT_TYPE_MYQEE = 'myqee'

export const ACCOUNT_TYPE_UNIFYD = 'unifyd'
export const ACCOUNT_TYPE_UNIFYD_GAIA = 'unifyd_gaia'
export const ACCOUNT_TYPE_UNIFYD_STRIPE = 'unifyd_stripe'
export const ACCOUNT_TYPE_UNIFYD_PAYPAL = 'unifyd_paypal'
export const ACCOUNT_TYPE_UNIFYD_ITUNES = 'unifyd_itunes'
export const ACCOUNT_TYPE_UNIFYD_GOOGLE = 'unifyd_google'
export const ACCOUNT_TYPE_UNIFYD_AMAZON = 'unifyd_amazon'
export const ACCOUNT_TYPE_UNIFYD_ROKU = 'unifyd_roku'
export const ACCOUNT_TYPE_UNIFYD_ANDROID = 'unifyd_android'

// Unifyd Migration Account Type
export const ACCOUNT_TYPE_UNIFYD_FREE_ACCESS = 'unifyd_free_access'
export const ACCOUNT_TYPE_UNIFYD_THIRD_PARTY = 'unifyd_third_party'
export const ACCOUNT_TYPE_UNIFYD_MIGRATION = 'unifyd_migration'
export const ACCOUNT_TYPE_UNIFYD_PAYPAL_COMP = 'unifyd_paypal_comp'

// Unifyd Provider Names
export const UNIFYD_PROVIDER_STRIPE = 'stripe'
export const UNIFYD_PROVIDER_ITUNES = 'itunes'
export const UNIFYD_PROVIDER_GOOGLE = 'google'
export const UNIFYD_PROVIDER_AMAZON = 'amazon'
export const UNIFYD_PROVIDER_ROKU = 'roku'
export const UNIFYD_PROVIDER_PAYPAL = 'paypal'
export const UNIFYD_PROVIDER_ANDROID = 'android'

// Unifyd Provider Map (for granular feature flag control)
export const UNIFYD_PROVIDER_MAP = {
  [ACCOUNT_TYPE_UNIFYD_STRIPE]: UNIFYD_PROVIDER_STRIPE,
  [ACCOUNT_TYPE_UNIFYD_ITUNES]: UNIFYD_PROVIDER_ITUNES,
  [ACCOUNT_TYPE_UNIFYD_GOOGLE]: UNIFYD_PROVIDER_GOOGLE,
  [ACCOUNT_TYPE_UNIFYD_AMAZON]: UNIFYD_PROVIDER_AMAZON,
  [ACCOUNT_TYPE_UNIFYD_ROKU]: UNIFYD_PROVIDER_ROKU,
  [ACCOUNT_TYPE_UNIFYD_PAYPAL]: UNIFYD_PROVIDER_PAYPAL,
  [ACCOUNT_TYPE_UNIFYD_ANDROID]: UNIFYD_PROVIDER_ANDROID,
}

const providerLockdownFlags = {
  [UNIFYD_PROVIDER_ITUNES]: !!unifydToggles.migrationStepTwoApple,
  [UNIFYD_PROVIDER_GOOGLE]: !!unifydToggles.migrationStepTwoGoogle,
  [UNIFYD_PROVIDER_AMAZON]: !!unifydToggles.migrationStepTwoAmazon,
  [UNIFYD_PROVIDER_ROKU]: !!unifydToggles.migrationStepTwoRoku,
  [UNIFYD_PROVIDER_STRIPE]: !!unifydToggles.migrationStepTwoStripe,
  [UNIFYD_PROVIDER_PAYPAL]: !!unifydToggles.migrationStepTwoPaypal,
  [UNIFYD_PROVIDER_ANDROID]: !!unifydToggles.migrationStepTwoAndroid,
}

// Pause constants
export const USER_ACCOUNT_PAUSE_ONE_MONTH = 1
export const USER_ACCOUNT_PAUSE_TWO_MONTHS = 2
export const USER_ACCOUNT_PAUSE_THREE_MONTHS = 3

export const SCHEMA_USER_ACCOUNT_CANCEL_V2 = {
  formName: FORM_SUBMISSION_NAME_CANCEL_FLOW,
  billingAccountId: '',
  subscriptionId: '',
  reason: '',
  comment: '',
}

const SCHEMA_CANCEL_CONFIRM = {
  comments: null,
  create: null,
  reason: null,
  start: null,
  subscriptionUuid: null,
  uuid: null,
}

const SCHEMA_ZUORA_CANCEL_CONFIRM = {
  success: null,
}

export const SCHEMA_PAUSE = {
  durationInMonths: null,
  reason: null,
}

const SCHEMA_PAUSE_CONFIRM = {
  startDate: null,
  endDate: null,
}

const SCHEMA_BILLING_SUBSCRIPTIONS_WITH_DETAILS_ZUORA = {
  subscriptionId: null,
  subscriptionNumber: null,
  startDate: null,
  endDate: null,
  nextReviewDate: null,
  paidThroughDate: null,
  nextBillAmount: null,
  billingAccountId: null,
  billingAccountNumber: null,
  billingAddress: null,
  countryIso: null,
  currencyIso: null,
  status: null,
  acceptedCancellationOffer: null,
  plan: null,
  nextPlan: null,
  futurePlan: null,
  paymentSource: null,
  errors: null,
  latamPricing: null,
  pauseDate: null,
  pauseMonths: null,
  resumeDate: null,
  cancelDate: null,
  xff: null,
  giftStartDate: null,
  giftEndDate: null,
  initialAuthSuccess: null,
}

const SCHEMA_GIFT_SUBSCRIPTIONS_WITH_DETAILS = {
  status: null,
  paidThroughDate: null,
  currentGift: null,
  nextGift: null,
  futureGifts: null,
}

const SCHEMA_GIFT_RECIPIENT_SUBSCRIPTION_CHECK = {
  activeSubscription: null,
}

const SCHEMA_SUBSCRIPTION_PAYMENTS = {
  error: false,
  success: false,
  data: [],
}

const SCHEMA_INVOICE_HISTORY = {
  error: false,
  success: false,
  invoices: [],
}

const SCHEMA_INVOICE = {
  error: false,
  success: false,
  data: {},
}

const SCHEMA_VALIDATION = {
  error: false,
  success: false,
  data: {},
}

const SCHEMA_CHANGE_PLAN_PRORATE_PRICE = {
  error: null,
  success: null,
  amountWithoutTax: null,
  taxAmount: null,
  targetDate: null,
}

/**
 * Executes user account cancellation flow from any page
 * Prepares cancellation data, navigates to confirm page, and triggers cancellation
 * @param {Object} options - Options object
 * @param {Object} options.props - Component props with userAccount, auth, history, actions
 * @param {Function} options.historyRedirect - Navigation function from services/navigation
 * @param {string} options.userLanguage - User's primary language
 * @param {string} options.cancelConfirmUrl - URL for cancel confirmation page
 * @param {string} [options.reason='other'] - Cancellation reason
 * @param {string} [options.comment=''] - Additional comment
 */
export function executeUserAccountCancel ({
  props,
  historyRedirect,
  userLanguage,
  cancelConfirmUrl,
  reason = 'other',
  comment = '',
}) {
  const {
    userAccount,
    auth,
    history,
    setUserAccountCancelReason,
    setUserAccountCancelFormData,
    setUserAccountCancel,
  } = props

  // Get billing values
  const billingAccountId = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'billingAccountId'], '')
  const subscriptionId = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'subscriptionId'], '')

  // Prepare cancellation data
  const cancelData = {
    ...SCHEMA_USER_ACCOUNT_CANCEL_V2,
    billingAccountId,
    subscriptionId,
    reason,
    comment,
  }

  // Set cancellation data
  setUserAccountCancelReason(reason)
  setUserAccountCancelFormData(cancelData)

  // Navigate to confirm page
  historyRedirect({
    history,
    url: cancelConfirmUrl,
    auth,
    language: userLanguage,
  })

  // Execute cancellation after navigation (watcher needs user on /account/cancel/* page)
  setTimeout(() => {
    setUserAccountCancel()
  }, 100)
}

export function userShouldReactivate (props) {
  const { userAccount, auth } = props
  const billingAccountId = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'billingAccountId'])
  const userIsEntitled = isEntitled(auth.get('subscriptions', List()))
  const subscriptionStatus = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'status'])
  const subscriptionCancelled = subscriptionStatus === PLAN_SUBSCRIPTION_CANCELLED

  return (billingAccountId && !userIsEntitled && subscriptionCancelled)
}

function useUserAccountPauseData (userAccount) {
  const accountPause = _get(config, ['features', 'userAccount', 'accountPause'])
  const pauseDate = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'pauseDate'])
  const pauseMonths = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'pauseMonths'])
  const subscriptionStatus = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'status'])
  return [accountPause, pauseDate, pauseMonths, subscriptionStatus]
}

export function hasActivePause (userAccount) {
  const [
    accountPause,
    pauseDate,
    pauseMonths,
    subscriptionStatus,
  ] = useUserAccountPauseData(userAccount)

  if (
    accountPause
    && pauseDate
    && pauseMonths
    && (subscriptionStatus === USER_ACCOUNT_PAUSED)
  ) {
    return true
  }

  return false
}

export function hasScheduledPause (userAccount) {
  const [
    accountPause,
    pauseDate,
    pauseMonths,
    subscriptionStatus,
  ] = useUserAccountPauseData(userAccount)

  if (
    accountPause
    && pauseDate
    && pauseMonths
    && subscriptionStatus === USER_ACCOUNT_ACTIVE
  ) {
    return true
  }

  return false
}

export function hasScheduledOrActivePause (userAccount) {
  const [
    accountPause,
    pauseDate,
    pauseMonths,
    subscriptionStatus,
  ] = useUserAccountPauseData(userAccount)

  if (
    accountPause
    && pauseDate
    && pauseMonths
    && ((subscriptionStatus === USER_ACCOUNT_ACTIVE)
      || (subscriptionStatus === USER_ACCOUNT_PAUSED))
  ) {
    return true
  }

  return false
}

// This function determines if the user is allowed to pause their account
export function userPauseAllowed (props) {
  const { userAccount } = props
  const accountPause = _get(config, ['features', 'userAccount', 'accountPause'])
  const currentPlan = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan'], Map()) || Map()
  const currentPlanCategory = currentPlan.get('planCategory')
  const currentPlanSku = currentPlan.get('planSku')
  const nextPlan = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'nextPlan'], Map()) || Map()
  const nextPlanCategory = nextPlan.get('planCategory')
  const nextPlanSku = nextPlan.get('planSku')
  const futurePlan = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'futurePlan'], Map()) || Map()
  const futurePlanCategory = futurePlan.get('planCategory')
  const futurePlanSku = futurePlan.get('planSku')
  const subscriptionStatus = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'status'])
  const zuoraAccountId = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'billingAccountId'])
  const inTrialSegment = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'inTrialSegment'])
  const paidThroughDate = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'paidThroughDate'])
  const payments = userAccount.getIn(['details', 'data', 'billing', 'payments', 'data'], Map())
  const paymentTypeExternal = payments.getIn([0, 'paymentType']) === 'External'
  const hasGiftSubscription = !!userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'giftStartDate'])
  // inMonthlyPlan: "monthly" = we find a monthly non-comp plan via this cascade:
  // (1) current plan is monthly and not comp → monthly;
  // (2) if current is comp, next plan is monthly and not comp → monthly;
  // (3) if current and next are both comp, future plan is monthly and not comp → monthly;
  const currentIsComp = currentPlanSku === PLAN_SKU_COMPLIMENTARY
  const nextIsComp = nextPlanSku === PLAN_SKU_COMPLIMENTARY
  const currentIsMonthlyNonComp = currentPlanCategory === PLAN_CATEGORY_MONTHLY
    && currentPlanSku !== PLAN_SKU_COMPLIMENTARY
  const nextIsMonthlyNonComp = nextPlanCategory === PLAN_CATEGORY_MONTHLY
    && nextPlanSku !== PLAN_SKU_COMPLIMENTARY
  const futureIsMonthlyNonComp = futurePlanCategory === PLAN_CATEGORY_MONTHLY
    && futurePlanSku !== PLAN_SKU_COMPLIMENTARY
  const inMonthlyPlan = currentIsMonthlyNonComp
    || (currentIsComp && nextIsMonthlyNonComp)
    || (currentIsComp && nextIsComp && futureIsMonthlyNonComp)

  if (
    accountPause
    && zuoraAccountId
    && inMonthlyPlan
    && !hasGiftSubscription
    && !inTrialSegment
    && subscriptionStatus === USER_ACCOUNT_ACTIVE
    && !paymentTypeExternal
    && getDateTime(paidThroughDate) > getCurrentTime()
  ) {
    return true
  }
  return false
}

export function shouldRenderNextPlanSection (props) {
  const { userAccount } = props
  const currentPlanId = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'productRatePlanId'])
  const nextPlanId = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'nextPlan', 'productRatePlanId'])
  const nextPlan = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'nextPlan'])

  if (nextPlan && currentPlanId !== nextPlanId) {
    return true
  }

  return false
}

// this function appears to be out of date, and shouldn't be used
// until it is updated to match whatever the current/future needs are
// The function is indended to help show a message to the user when they are
// Within 15 days from the end of their non-recurring subscription
// In the future, that could be a gift subscription, etc.
export function showUserAccountRenewMessage (props) {
  const { userAccount } = props
  const planType = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'productRatePlanId'])
  const endDate = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'endDate'])
  const remainingTime = Date.parse(endDate) - Date.now()
  const fifteenDaysInMilliseconds = 1296000000
  let recurrenceType = null

  if (
    userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'charges', 0, 'type']) === USER_ACCOUNT_RECURRING_PLAN
    || userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'charges', 1, 'type']) === USER_ACCOUNT_RECURRING_PLAN
    || userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'nextPlan', 'charges', 0, 'type']) === USER_ACCOUNT_RECURRING_PLAN
  ) {
    recurrenceType = USER_ACCOUNT_RECURRING_PLAN
  }

  if ((
    planType !== PLAN_ID_MONTHLY
    && planType !== PLAN_ID_ANNUAL
    && recurrenceType !== USER_ACCOUNT_RECURRING_PLAN
    && remainingTime > 0
    && endDate
  )
  || (
    remainingTime <= fifteenDaysInMilliseconds
    && remainingTime > 0
    && recurrenceType !== USER_ACCOUNT_RECURRING_PLAN
    && endDate
  )) {
    return true
  }

  return false
}

export function showChangePlanUpgradeMessage (props) {
  const { userAccount } = props
  const planCategory = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'planCategory'])
  const futurePlanCategory = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'futurePlan', 'planCategory'])
  const nextPlanCategory = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'nextPlan', 'planCategory'])
  const lastTransactionStatus = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'paymentSource', 'lastTransactionStatus'])
  const subscriptionStatus = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'status'])
  const subscriptionCancelled = subscriptionStatus === PLAN_SUBSCRIPTION_CANCELLED
  const reactivateRequired = userShouldReactivate(props)
  const hasGiftSubscription = useSelector(selectHasGiftSubscription)
  const annualOrGaiaPlus = planCategory === PLAN_CATEGORY_ANNUAL
    || planCategory === PLAN_CATEGORY_GAIA_PLUS
  const nextAnnualOrGaiaPlus = nextPlanCategory === PLAN_CATEGORY_ANNUAL
    || nextPlanCategory === PLAN_CATEGORY_GAIA_PLUS
  const futureAnnualOrGaiaPlus = futurePlanCategory === PLAN_CATEGORY_ANNUAL
    || futurePlanCategory === PLAN_CATEGORY_GAIA_PLUS

  // If the last transaction status is declined, don't show the message
  if (lastTransactionStatus === USER_ACCOUNT_TRANSACTION_STATUS_DECLINED) {
    return false
  }

  // If the user requires reactivation,
  // has a gift subscription, or is cancelled, don't show the message
  if (reactivateRequired || hasGiftSubscription || subscriptionCancelled) {
    return false
  }

  // If the user is on or will be on an annual or Gaia Plus plan, don't show the message
  if (annualOrGaiaPlus || nextAnnualOrGaiaPlus || futureAnnualOrGaiaPlus) {
    return false
  }

  return true
}

export function showChangePlanLink (props) {
  const { userAccount } = props
  const lastTransactionStatus = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'paymentSource', 'lastTransactionStatus'])
  const lastTransactionDeclined = lastTransactionStatus === USER_ACCOUNT_TRANSACTION_STATUS_DECLINED
  const startDate = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'startDate'])
  const date = formatDateWithTimezonOffset(new Date())
  const formattedStartDate = getDateTime(startDate)
  const formattedCurrentDate = getDateTime(date)
  const futureStartDate = (formattedStartDate > formattedCurrentDate)

  return (
    !lastTransactionDeclined && !futureStartDate
  )
}

export function showFreeTrialUpgradeMessage (props) {
  const { user, userAccount } = props
  const zuoraAccount = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'billingAccountId'])
  const freeTrial = user.getIn(['data', 'freeTrialNoBillingInfo', 'ratePlanId'])

  if (!zuoraAccount && freeTrial) {
    return true
  }

  return false
}

export function showFreeTrialEndingMessage (props, locale) {
  const { user, userAccount } = props
  const zuoraAccount = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'billingAccountId'])
  const freeTrial = user.getIn(['data', 'freeTrialNoBillingInfo', 'ratePlanId'])
  const twentyFourHoursInMilliseconds = 86400000
  const dateFormatString = 'MM/DD/YY'
  const mostRecentEntitlement = selectMostRecentEligibleEntitlementFromUser(user)
  const endDateString = mostRecentEntitlement ? mostRecentEntitlement.get('end') : ''
  const endDateInMilliseconds = parseInt(endDateString, 10) * 1000
  const formattedEndDate = new Date(endDateInMilliseconds)
  const getEndDate = getDateTime(formattedEndDate)
  const reformattedEndDate = formatDateTime(getEndDate, locale, dateFormatString)
  const remainingTime = Date.parse(reformattedEndDate) - Date.now()

  if (!zuoraAccount && freeTrial) {
    if (remainingTime < twentyFourHoursInMilliseconds && remainingTime > 0) {
      return true
    }
  }

  return false
}

export function showFmtvReactivateMessage (props) {
  const { userAccount, user } = props
  const currentDate = new Date()
  const currentDateTimeStamp = getDateTime(currentDate)
  const creationSource = user.getIn(['data', 'creation_source'])
  const creationSourceFMTV = _startsWith(creationSource, ACCOUNT_TYPE_FMTV, 0)
  const externalEmail = user.getIn(['data', 'email_registered_externally'])
  const billingAccountId = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'billingAccountId'])
  const fmtvBillingMigrationCompletionDate = _get(config, ['features', 'userAccount', 'fmtvMigrationDate'])

  if (
    (currentDateTimeStamp >= fmtvBillingMigrationCompletionDate)
    && creationSourceFMTV
    && !billingAccountId
    && !externalEmail
  ) {
    return true
  } if (
    (currentDateTimeStamp >= fmtvBillingMigrationCompletionDate)
    && !creationSourceFMTV
    && !billingAccountId
    && externalEmail
  ) {
    return true
  }
  return false
}

export function showReactivateMessage (props) {
  const { paytrack } = props
  const providerName = paytrack.getIn(['lastTransaction', 'provider_name'])
  const reactivateRequired = userShouldReactivate(props)

  if (reactivateRequired || providerName === PAYMENT_PROVIDER_NAME_GCSI) {
    return true
  }

  return false
}

export function userHasPaymentError (props) {
  const { userAccount } = props
  const subscriptionStatus = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'status'])
  const lastTransactionStatus = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'paymentSource', 'lastTransactionStatus'])
  const initialAuthSuccess = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'initialAuthSuccess'])
  const subscriptionCancelled = subscriptionStatus === PLAN_SUBSCRIPTION_CANCELLED

  if (
    (lastTransactionStatus === USER_ACCOUNT_TRANSACTION_STATUS_DECLINED
      || initialAuthSuccess === false
    )
      && !subscriptionCancelled
  ) {
    return true
  }

  return false
}

export function showUpdatePaymentMethodMessage (props) {
  const { userAccount } = props
  const expMonth = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'paymentSource', 'expMonth'])
  const expYear = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'paymentSource', 'expYear'])
  const currentDate = new Date()
  const currentMonth = currentDate.getMonth() + 1
  const currentYear = currentDate.getFullYear()
  const subscriptionStatus = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'status'])
  const subscriptionCancelled = subscriptionStatus === PLAN_SUBSCRIPTION_CANCELLED

  if (currentYear === expYear && currentMonth === expMonth && !subscriptionCancelled) {
    return true
  }

  return false
}

export function isTaxable (props) {
  const {
    auth = Map(),
    userAccount = Map(),
    user = Map(),
  } = props
  const userRequestCountry = user.getIn(['data', 'requestCountry'])
  const authCountry = auth.get('country')
  const userAccountCountry = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'countryIso'])
  const country = userAccountCountry || authCountry || userRequestCountry || US

  // country is a taxable country
  return _includes(TAXABLE_COUNTRIES, country)
}

function modelBillingAccountUpdatesZuoraPaypal (error, postalCode, body) {
  const billToUpdateSuccess = _get(body, 'billToUpdateSuccess', false)
  const soldToContactSuccess = _get(body, 'soldToContactSuccess', false)
  return {
    errorCode: error ? _get(body, 'errorCode')
      || (billToUpdateSuccess === false && 'BILL_TO_UPDATE')
      || (soldToContactSuccess === false && 'SOLD_TO_CONTACT')
      || '' : null,
    success: !error,
    postalCode,
    error,
  }
}

export async function updateBillingAccountZuoraPaypal (options = {}) {
  const {
    auth, accountId, accountNumber, postalCode,
  } = options
  try {
    if (!accountId) throw new Error('accountId is required.')
    if (!accountNumber) throw new Error('accountNumber is required.')
    const res = await apiPut(`v1/billing/account/address?accountId=${accountId}&accountNumber=${accountNumber}`, { postalCode }, { auth }, TYPE_BROOKLYN_JSON)
    return modelBillingAccountUpdatesZuoraPaypal(res.status !== 200, postalCode, res.body)
  } catch (err) {
    return modelBillingAccountUpdatesZuoraPaypal(true, postalCode, {})
  }
}

export async function setZuoraUserAccountSubscriptionCancelConfirmed (options = {}) {
  try {
    const { auth } = options
    const res = await apiPut('v1/user/subscription', null, { auth }, TYPE_BROOKLYN_JSON)
    return handleZuoraSetSubscriptionCancelResponse(res)
  } catch (err) {
    return handleSetSubscriptionCancelResponse({})
  }
}

export function handleZuoraSetSubscriptionCancelResponse (res) {
  const { statusCode } = res
  const data = _get(res, ['body'], {})

  if (statusCode > 299 || _get(data, 'status') > 299) {
    return { success: false }
  }

  return _assign(_cloneDeep(SCHEMA_ZUORA_CANCEL_CONFIRM), {
    success: _get(data, 'success'),
  })
}

export function handleSetSubscriptionCancelResponse (res) {
  const data = _get(res, ['body', 'data'], {})
  return _assign(_cloneDeep(SCHEMA_CANCEL_CONFIRM), {
    comments: _get(data, 'comments'),
    create: _get(data, 'create'),
    reason: _get(data, 'reason'),
    start: _get(data, 'start'),
    paidThroughDate: _get(data, 'paidThroughDate'),
    subscriptionUuid: _get(data, 'subscriptionUuid'),
    uuid: _get(data, 'uuid'),
  })
}

export function handleSetSubscriptionsWithDetailsZuoraResponse (res, _dataError) {
  const data = _get(res, 'body', {})
  const subscriptions = _assign(_cloneDeep(SCHEMA_BILLING_SUBSCRIPTIONS_WITH_DETAILS_ZUORA), {
    subscriptionId: _get(data, 'subscriptionId'),
    subscriptionNumber: _get(data, 'subscriptionNumber'),
    startDate: _get(data, 'startDate'),
    endDate: _get(data, 'endDate'),
    nextReviewDate: _get(data, 'nextReviewDate'),
    paidThroughDate: _get(data, 'paidThroughDate'),
    nextBillAmount: _get(data, 'nextBillAmount'),
    billingAccountId: _get(data, 'billingAccountId'),
    billingAccountNumber: _get(data, 'billingAccountNumber'),
    billingAddress: _get(data, 'billingAddress', {}),
    countryIso: _get(data, 'countryIso'),
    currencyIso: _get(data, 'currencyIso'),
    status: _get(data, 'status'),
    acceptedCancellationOffer: _get(data, 'acceptedCancellationOffer'),
    plan: _get(data, 'plan'),
    nextPlan: _get(data, 'nextPlan'),
    futurePlan: _get(data, 'futurePlan'),
    previousPlan: _get(data, 'previousPlan'),
    planHistory: _get(data, 'planHistory'),
    paymentSource: _get(data, 'paymentSource'),
    errors: _get(data, ['errors']),
    latamPricing: _get(data, 'latamPricing'),
    pauseDate: _get(data, 'pauseDate'),
    pauseMonths: _get(data, 'pauseMonths'),
    resumeDate: _get(data, 'resumeDate'),
    cancelDate: _get(data, 'cancelDate'),
    xff: _get(data, 'xff'),
    giftStartDate: _get(data, 'giftStartDate'),
    giftEndDate: _get(data, 'giftEndDate'),
    initialAuthSuccess: _get(data, 'initialAuthSuccess'),
  })

  return fromJS({
    _dataError,
    statusCode: _get(res, 'statusCode'),
    subscriptions,
  })
}

export function handleSetGiftSubscriptionsWithDetails (res, _dataError) {
  const data = _get(res, 'body', {})
  const gift = _assign(_cloneDeep(SCHEMA_GIFT_SUBSCRIPTIONS_WITH_DETAILS), {
    status: _get(data, 'status'),
    paidThroughDate: _get(data, 'paidThroughDate'),
    currentGift: _get(data, 'currentGift'),
    nextGift: _get(data, 'nextGift'),
    futureGifts: _get(data, 'futureGifts'),
  })

  return fromJS({
    _dataError,
    gift,
  })
}

/**
 * PAUSE
 */
export async function updateUserAccountPause (options = {}) {
  const { auth, data } = options
  const res = await apiPost('/v1/user/subscription/pause', data, { auth }, TYPE_BROOKLYN_JSON)
  const body = _get(res, 'body')
  return body
}

export function handleSetSubscriptionPauseResponse (res) {
  const data = _get(res, ['body'], {})
  return _assign(_cloneDeep(SCHEMA_PAUSE_CONFIRM), {
    startDate: _get(data, 'startDate'),
    endDate: _get(data, 'endDate'),
  })
}

/**
 * RESUME
 */
export async function updateUserAccountResume (options = {}) {
  const { auth, data } = options
  const res = await apiPut('/v1/user/subscription/resume', data, { auth }, TYPE_BROOKLYN_JSON)
  const body = _get(res, 'body')
  return body
}

export async function getUserAccountBillingSubscriptionsWithDetailsZuora (options = {}) {
  try {
    const { auth } = options
    if (!auth) {
      return handleSetSubscriptionsWithDetailsZuoraResponse({}, true)
    }
    const res = await apiGet('v1/user/subscription', null, { auth, timeout: 15000, retryDelay: 500 }, TYPE_BROOKLYN)
    return handleSetSubscriptionsWithDetailsZuoraResponse(res)
  } catch (e) {
    return handleSetSubscriptionsWithDetailsZuoraResponse({}, true)
  }
}

export async function getUserAccountGiftSubscriptionsWithDetails (options = {}) {
  try {
    const { auth } = options
    if (!auth) {
      return handleSetGiftSubscriptionsWithDetails({}, true)
    }
    const res = await apiGet('v1/gift/summary', null, { auth }, TYPE_BROOKLYN)
    return handleSetGiftSubscriptionsWithDetails(res)
  } catch (e) {
    return handleSetGiftSubscriptionsWithDetails({}, true)
  }
}

export async function changeUserAccountPlanTypeDataZuora (options = {}) {
  const handleResponse = function handleResponse (res, _dataError) {
    const data = _get(res, 'body', {})
    const success = _get(data, 'success')
    const orderNumber = _get(data, 'orderNumber')
    const errorCode = _get(data, ['errorCode'])

    if (!success) {
      return fromJS({
        _dataError: true,
        errorCode,
        immediate: false,
        success,
        orderNumber,
      })
    }

    if (_get(data, 'data', 'plan')) {
      return fromJS({
        _dataError,
        immediate: false,
        success,
        orderNumber,
      })
    }

    return fromJS({
      _dataError,
      immediate: true,
      success,
      orderNumber,
    })
  }

  try {
    const {
      nextRatePlanId,
      planSku,
      auth,
      tracking,
    } = options
    if (!auth) {
      return handleResponse({}, true)
    }

    const apiOptions = {
      auth,
    }
    const res = await apiPut(
      'v1/user/plan',
      { nextRatePlanId, planSku, tracking },
      apiOptions,
      TYPE_BROOKLYN_JSON,
    )
    return handleResponse(res)
  } catch (e) {
    return handleResponse({}, true)
  }
}

export function getUserType (paytrack) {
  const lastTransaction = paytrack.get('lastTransaction')
  return lastTransaction ? lastTransaction.get('provider_name') : null
}

export function getCurrentPlanInfoFromPlansCall (props, planId) {
  const { plans } = props
  const plansList = plans.getIn(['data', 'plans'], List())
  const planData = plansList.filter((plan) => {
    const id = plan.get('id')
    if (id === planId) {
      return plan
    }
    return null
  })
  return planData
}

export function handleUserSubscriptionCheckResponse (res, _dataError) {
  const data = _get(res, ['body'], {})
  return _assign(_cloneDeep(SCHEMA_GIFT_RECIPIENT_SUBSCRIPTION_CHECK), {
    _dataError,
    activeSubscription: _get(data, 'activeSubscription'),
  })
}

export async function checkUserSubscriptionStatus (options = {}) {
  try {
    const { email } = options
    const res = await apiPost('v1/user/subscription/check', { email }, null, TYPE_BROOKLYN_JSON)
    return handleUserSubscriptionCheckResponse(res)
  } catch (err) {
    return handleUserSubscriptionCheckResponse({}, true)
  }
}

export function handleUserSubscriptionPaymentsResponse (res, _dataError) {
  const data = _get(res, ['body'], {})
  const success = !_dataError
  return _assign(_cloneDeep(SCHEMA_SUBSCRIPTION_PAYMENTS), {
    error: _dataError,
    success,
    data: data || [],
  })
}

export async function getUserSubscriptionPayments (options = {}) {
  try {
    const { accountId, accountNumber, auth } = options
    const res = await apiGet('v1/payments', { accountId, accountNumber }, { auth }, TYPE_BROOKLYN_JSON)
    return handleUserSubscriptionPaymentsResponse(res)
  } catch (err) {
    return handleUserSubscriptionPaymentsResponse({}, true)
  }
}

export function handleUserAccountInvoiceHistoryResponse (res, _dataError) {
  const data = _get(res, ['body'], {})
  const success = _get(data, 'success', false)
  const invoices = _get(data, 'invoices', [])
  return _assign(_cloneDeep(SCHEMA_INVOICE_HISTORY), {
    error: _dataError,
    success,
    invoices,
  })
}

export async function getUserAccountInvoiceHistory (options = {}) {
  try {
    const { accountId, accountNumber, auth } = options
    const res = await apiGet('v1/billing/account/invoice-history', { accountId, accountNumber, paidOnly: true }, { auth }, TYPE_BROOKLYN_JSON)
    return handleUserAccountInvoiceHistoryResponse(res)
  } catch (err) {
    return handleUserAccountInvoiceHistoryResponse({}, true)
  }
}

export function handleUserAccountInvoiceResponse (res, _dataError) {
  const data = _get(res, ['body'], {})
  const success = _get(data, 'success', false)
  return _assign(_cloneDeep(SCHEMA_INVOICE), {
    error: _dataError,
    success,
    data: _get(data, 'invoice', {}),
  })
}

export async function getUserAccountInvoice (options = {}) {
  try {
    const {
      accountId, accountNumber, invoiceId, auth,
    } = options
    const res = await apiGet('v1/billing/account/invoices', {
      providerName: 'zuora', invoiceId, accountId, accountNumber,
    }, { auth }, TYPE_BROOKLYN_JSON)
    return handleUserAccountInvoiceResponse(res)
  } catch (err) {
    return handleUserAccountInvoiceResponse({}, true)
  }
}

export function handleUserAccountValidation (res, _dataError) {
  const data = _get(res, ['body'], {})
  const success = _get(data, 'success', false)

  return _assign(_cloneDeep(SCHEMA_VALIDATION), {
    error: _dataError,
    success,
    data,
  })
}

export async function getUserAccountValidation (options = {}) {
  const {
    postalCode,
    country,
    productRatePlanId,
    auth,
  } = options
  const data = {
    billing: {
      postalCode,
      country,
      productRatePlanId,
    },
  }

  try {
    const res = await apiPost('v1/billing/account/validate', data, { auth }, TYPE_BROOKLYN_JSON)
    return handleUserAccountValidation(res)
  } catch (err) {
    return handleUserAccountValidation({}, true)
  }
}

export function handleUserAccountChangePlanProratePrice (res, _dataError) {
  const data = _get(res, ['body'], {})
  return _assign(_cloneDeep(SCHEMA_CHANGE_PLAN_PRORATE_PRICE), {
    error: _dataError,
    success: _get(data, 'success'),
    amountWithoutTax: _get(data, 'amountWithoutTax'),
    taxAmount: _get(data, 'taxAmount'),
    targetDate: _get(data, 'targetDate'),
    activateImmediately: _get(data, 'activateImmediately'),
  })
}

export async function getUserAccountChangePlanProratePrice (options = {}) {
  const { auth, nextRatePlanId } = options
  try {
    const res = await apiGet('v1/user/plan/preview', { nextRatePlanId }, { auth }, TYPE_BROOKLYN_JSON)
    return handleUserAccountChangePlanProratePrice(res)
  } catch (err) {
    return handleUserAccountChangePlanProratePrice({}, true)
  }
}

export function shouldRenderCancelThreeMonthOffer (props) {
  // Feature flag must be enabled
  // Priority to price increase offer
  if (!isCancelThreeMonthOfferEnabled || shouldRenderCancelPriceIncreaseOffer(props)) {
    return false
  }

  const { user = Map(), userAccount = Map() } = props
  const userBillingAccountId = user.getIn(['data', 'billing_account_id'])
  const paidThroughDate = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'paidThroughDate'])
  const now = getDateTime(new Date())
  const nowUTC = getDateTimeUTC(now)
  const paidThroughDateUTC = paidThroughDate ? getDateTimeUTC(paidThroughDate) : null
  const futurePaidThroughDate = paidThroughDateUTC !== null && paidThroughDateUTC > nowUTC
  const productRatePlanId = userAccount.getIn([
    'details',
    'data',
    'billing',
    'subscriptions',
    'plan',
    'productRatePlanId',
  ])
  const currentPlanSku = userAccount.getIn([
    'details',
    'data',
    'billing',
    'subscriptions',
    'plan',
    'planSku',
  ])
  const nextProductRatePlanId = userAccount.getIn([
    'details',
    'data',
    'billing',
    'subscriptions',
    'nextPlan',
    'productRatePlanId',
  ])
  const nextPlanSku = userAccount.getIn([
    'details',
    'data',
    'billing',
    'subscriptions',
    'nextPlan',
    'planSku',
  ])
  const isPaymentApproved = USER_ACCOUNT_TRANSACTION_STATUS_APPROVED === userAccount.getIn([
    'details',
    'data',
    'billing',
    'subscriptions',
    'paymentSource',
    'lastTransactionStatus',
  ])
  const payments = userAccount.getIn(['details', 'data', 'billing', 'payments', 'data'], List())
  const paymentProcessed = payments.find((payment) => payment.get('status') === 'Processed')
  const noComp = nextProductRatePlanId !== PLAN_ID_COMPLIMENTARY
  const inTrialSegment = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'inTrialSegment']) || false

  // Check plan type for annual offer eligibility
  const subscriptionType = getPlanSubscriptionType(productRatePlanId)
  const isAnnualPlan = subscriptionType === PLAN_SUBSCRIPTION_ANNUAL

  // SKUs that should not see the annual offer
  const threeMonthSpecialSkus = [
    PLAN_SKU_THREE_MONTH_TO_ANNUAL_SPECIAL,
    PLAN_SKU_THREE_MONTH_TO_MONTHLY_SPECIAL,
  ]
  const isNext3MonthOffer = threeMonthSpecialSkus.includes(nextPlanSku)
  const isCurrent3MonthOffer = threeMonthSpecialSkus.includes(currentPlanSku)

  // Sage Special Access users get the three month to monthly offer
  const isSageSpecialAccess = currentPlanSku === PLAN_SKU_SAGE_SPECIAL_ACCESS

  // Base eligibility conditions
  const baseEligibility = userBillingAccountId
    && isPaymentApproved
    && noComp
    && paymentProcessed
    && !hasScheduledOrActivePause(userAccount)
    && futurePaidThroughDate
    && !inTrialSegment

  // Annual users (not on 3-month special) get the annual offer
  const isEligibleForAnnualOffer = baseEligibility
    && isAnnualPlan
    && !isNext3MonthOffer
    && !isCurrent3MonthOffer

  // Sage Special Access users get the monthly offer
  const isEligibleForSageOffer = baseEligibility && isSageSpecialAccess

  return !!(isEligibleForAnnualOffer || isEligibleForSageOffer)
}

/**
 * Determine which three month offer SKUs to show based on current plan
 * @param {Map} userAccount
 * @returns {Object} Object containing offerSku and comparisonSku
 */
export function getThreeMonthOfferSkus (userAccount = Map()) {
  const currentPlanSku = userAccount.getIn([
    'details',
    'data',
    'billing',
    'subscriptions',
    'plan',
    'planSku',
  ])

  // Sage Special Access users get the three month to monthly offer
  if (currentPlanSku === PLAN_SKU_SAGE_SPECIAL_ACCESS) {
    return {
      offerSku: PLAN_SKU_THREE_MONTH_TO_MONTHLY_SPECIAL,
      comparisonSku: PLAN_SKU_INCREASED_PRICE_MONTHLY,
    }
  }

  // Default: annual users get the three month to annual offer
  return {
    offerSku: PLAN_SKU_THREE_MONTH_TO_ANNUAL_SPECIAL,
    comparisonSku: PLAN_SKU_INCREASED_PRICE_ANNUAL,
  }
}

export function shouldRenderCancelOffer (props, usePaidThroughDate = null) {
  const { user, userAccount } = props
  const userBillingAccountId = user.getIn(['data', 'billing_account_id'])
  const paidThroughDate = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'paidThroughDate'])
  const startDate = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'startDate'])
  const reformattedStartDate = startDate ? getDateTime(startDate) : undefined
  const now = getDateTime(new Date())
  // Use UTC for date comparison to avoid timezone edge cases
  // This ensures we compare dates correctly regardless of timezone differences
  const nowUTC = getDateTimeUTC(now)
  const paidThroughDateUTC = paidThroughDate ? getDateTimeUTC(paidThroughDate) : null
  const futurePaidThroughDate = paidThroughDateUTC !== null && paidThroughDateUTC > nowUTC
  const daysOnSubscription = reformattedStartDate
    ? getDifferenceInDays(now, reformattedStartDate) : undefined
  const isVested = daysOnSubscription >= 8
  const accountStatusNotActive = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'status']) !== USER_ACCOUNT_ACTIVE
  const productRatePlanId = userAccount.getIn([
    'details',
    'data',
    'billing',
    'subscriptions',
    'plan',
    'productRatePlanId',
  ])
  const nextProductRatePlanId = userAccount.getIn([
    'details',
    'data',
    'billing',
    'subscriptions',
    'nextPlan',
    'productRatePlanId',
  ])
  const cancelOfferAccepted = userAccount.getIn([
    'details',
    'data',
    'billing',
    'subscriptions',
    'acceptedCancellationOffer',
  ])
  const isPaymentApproved = USER_ACCOUNT_TRANSACTION_STATUS_APPROVED === userAccount.getIn([
    'details',
    'data',
    'billing',
    'subscriptions',
    'paymentSource',
    'lastTransactionStatus',
  ])
  const currentPlanSku = userAccount.getIn([
    'details',
    'data',
    'billing',
    'subscriptions',
    'plan',
    'planSku',
  ])
  const nextPlanSku = userAccount.getIn([
    'details',
    'data',
    'billing',
    'subscriptions',
    'nextPlan',
    'planSku',
  ])
  const noMonthLongTrial = productRatePlanId !== PLAN_ID_ONE_MONTH_TRIAL_TO_ANNUAL
      && productRatePlanId !== PLAN_ID_ONE_DOLLAR_THIRTY_DAYS_TO_MONTHLY
  const noSageSpecialAccess = currentPlanSku !== PLAN_SKU_SAGE_SPECIAL_ACCESS
  const payments = userAccount.getIn(['details', 'data', 'billing', 'payments', 'data'], List())
  const paymentProcessed = payments.find((payment) => {
    return payment.get('status') === 'Processed'
  })
  const noComp = productRatePlanId !== PLAN_ID_COMPLIMENTARY
      && nextProductRatePlanId !== PLAN_ID_COMPLIMENTARY
  const threeMonthSpecialSkus = [
    PLAN_SKU_THREE_MONTH_TO_ANNUAL_SPECIAL,
    PLAN_SKU_THREE_MONTH_TO_MONTHLY_SPECIAL,
  ]
  const noThreeMonthSpecial = !threeMonthSpecialSkus.includes(currentPlanSku)
    && !threeMonthSpecialSkus.includes(nextPlanSku)
  const noThreeMonthSpecialOffer = !shouldRenderCancelThreeMonthOffer(props)
  // For active members: require futurePaidThroughDate to prevent offering FMO
  // when PTD is today or in the past (which would create invoice payment issues)
  const standardCase = userBillingAccountId
    && !cancelOfferAccepted
    && isVested
    && isPaymentApproved
    && noComp
    && noMonthLongTrial
    && noSageSpecialAccess
    && paymentProcessed
    && !hasScheduledOrActivePause(userAccount)
    && futurePaidThroughDate // Ensure PTD is future, not today or past
    && noThreeMonthSpecial // Ensure user is not on the three month special offer
    && noThreeMonthSpecialOffer // Block if user has the three month special offer
  const extendedCase = standardCase && accountStatusNotActive

  if (usePaidThroughDate) {
    return extendedCase
  }

  if (standardCase) {
    return true
  }

  return false
}

export function isFirstPromotionPeriod (planCategory) {
  const now = getCurrentTime()
  const currentTime = new Date(now).getTime()

  // Monthly until Mar 31st 11pm 2026
  // Default until Mar 2nd 11pm 2026
  const monthlyEndDate = new Date('2026-03-31T23:00:00')
  const defaultEndDate = new Date('2026-03-02T23:00:00')

  if (planCategory === PLAN_CATEGORY_MONTHLY) {
    return currentTime <= monthlyEndDate.getTime()
  }

  return currentTime <= defaultEndDate.getTime()
}

export function isCancelPriceIncreaseCountryAllowed (userAccount = Map()) {
  const userAccountCountryIso = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'countryIso'], '')
  return !_includes([DE, AT, ME, XK], userAccountCountryIso)
}

export function shouldRenderCancelWarningModal (props) {
  const { userAccount } = props

  const isPriceIncreaseCountryAllowed = isCancelPriceIncreaseCountryAllowed(userAccount)
  const planCategory = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'planCategory']) || ''
  const isGaiaPlus = planCategory === PLAN_CATEGORY_GAIA_PLUS
  const isToggleEnabled = isCancelPriceIncreaseOfferEnabled(planCategory)
  const currentPlanSku = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'planSku']) || ''
  const hasOldPrice = PRIMARY_PLAN_SKUS.includes(currentPlanSku)

  return isToggleEnabled
    && hasOldPrice
    && !isGaiaPlus
    && !isPriceIncreaseCountryAllowed
}

export function shouldRenderCancelPriceIncreaseOffer (props) {
  const { user, userAccount } = props
  const userBillingAccountId = user.getIn(['data', 'billing_account_id'])
  const paidThroughDate = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'paidThroughDate'])
  const currentPlanId = userAccount.getIn([
    'details',
    'data',
    'billing',
    'subscriptions',
    'plan',
    'productRatePlanId',
  ])

  // Get subscription type and check if toggle is enabled for this plan type
  const planCategory = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'planCategory']) || ''
  if (!isCancelPriceIncreaseOfferEnabled(planCategory)) {
    return false
  }
  const currentPlanSku = userAccount.getIn([
    'details',
    'data',
    'billing',
    'subscriptions',
    'plan',
    'planSku',
  ])
  const nextPlanSku = userAccount.getIn([
    'details',
    'data',
    'billing',
    'subscriptions',
    'nextPlan',
    'planSku',
  ])
  const nextProductRatePlanId = userAccount.getIn([
    'details',
    'data',
    'billing',
    'subscriptions',
    'nextPlan',
    'productRatePlanId',
  ])
  const now = getDateTime(new Date())
  const nowUTC = getDateTimeUTC(now)
  const startDate = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'startDate'])
  const paidThroughDateUTC = paidThroughDate ? getDateTimeUTC(paidThroughDate) : null
  const reformattedStartDate = startDate ? getDateTime(startDate) : undefined
  const daysOnSubscription = reformattedStartDate
    ? getDifferenceInDays(now, reformattedStartDate) : undefined
  const payments = userAccount.getIn(['details', 'data', 'billing', 'payments', 'data'], List())

  // Country restrictions
  const isCountryAllowed = isCancelPriceIncreaseCountryAllowed(userAccount)

  // Billing restrictions
  const futurePaidThroughDate = paidThroughDateUTC !== null && paidThroughDateUTC > nowUTC
  const isVested = daysOnSubscription >= 8
  const paymentProcessed = payments.find((payment) => {
    return payment.get('status') === 'Processed'
  })
  const noScheduledOrActivePause = !hasScheduledOrActivePause(userAccount)

  // Plans restrictions
  const isNotPriceIncreasedAnnualPlan = currentPlanSku !== PLAN_SKU_PRICE_INCREASE_2026_ANNUAL
  const noComp = currentPlanId !== PLAN_ID_COMPLIMENTARY
    && nextProductRatePlanId !== PLAN_ID_COMPLIMENTARY
  const noThreeMonthToAnnualOffer = currentPlanSku !== PLAN_SKU_THREE_MONTH_TO_ANNUAL_SPECIAL
    && nextPlanSku !== PLAN_SKU_THREE_MONTH_TO_ANNUAL_SPECIAL
  const noThreeMonthTrial = currentPlanSku !== PLAN_SKU_THREE_MONTH_WITH_TRIAL
    && nextPlanSku !== PLAN_SKU_THREE_MONTH_WITH_TRIAL
  const noSpecialOffer = currentPlanSku !== PLAN_SKU_PRICE_INCREASE_2026_ANNUAL_SPECIAL
    && nextPlanSku !== PLAN_SKU_PRICE_INCREASE_2026_ANNUAL_SPECIAL
  const noNextThreeMonthToMonthlyOffer = nextPlanSku !== PLAN_SKU_THREE_MONTH_TO_MONTHLY_SPECIAL
  const noThreeMonthTrialCl = currentPlanSku !== PLAN_SKU_THREE_MONTH_WITH_TRIAL_CL
    && nextPlanSku !== PLAN_SKU_THREE_MONTH_WITH_TRIAL_CL

  return (
    isCountryAllowed
    && userBillingAccountId
    && futurePaidThroughDate
    && isVested
    && paymentProcessed
    && noScheduledOrActivePause
    && isNotPriceIncreasedAnnualPlan
    && noComp
    && noThreeMonthToAnnualOffer
    && noThreeMonthTrial
    && noSpecialOffer
    && noNextThreeMonthToMonthlyOffer
    && noThreeMonthTrialCl
  )
}

export function isPrimaryAccountProfile (props) {
  const { user } = props
  const uid = _parseInt(user.getIn(['data', 'uid']))
  const accountOwnerUid = _parseInt(user.getIn(['data', 'account_owner_uid']))

  return uid === accountOwnerUid
}

export function isGaiaDirectPayment (userAccount, paytrack) {
  const zuoraAccountId = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'billingAccountId'])
  const providerName = paytrack.getIn(['lastTransaction', 'provider_name'])
  const accountStatus = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'status'])
  const inGiftPeriod = userAccount.getIn(['details', 'data', 'gift', 'status']) === 'Active'
  const accountActive = accountStatus === USER_ACCOUNT_ACTIVE
    || accountStatus === USER_ACCOUNT_PENDING

  if (
    (zuoraAccountId && providerName === PAYMENT_PROVIDER_NAME_ZUORA)
    || (inGiftPeriod && !accountActive)
  ) {
    return true
  }

  return false
}

/**
 * Check if the user is entitled to a third party subscription
 * @param {Object} props - The props object
 * @param {Map} props.paytrack - The paytrack object
 * @param {Map} props.auth - The auth object
 * @returns {Boolean} - Boolean indicating if the user is entitled to a third party subscription
 */
export function isThirdPartyEntitled (props) {
  const { paytrack = Map(), auth = Map() } = props

  if (!paytrack || !auth) {
    return false
  }

  if (!Map.isMap(paytrack) || !Map.isMap(auth)) {
    return false
  }

  const providerName = paytrack.getIn(['lastTransaction', 'provider_name'])
  const standardSubscriptions = filterStandardSubscriptions(auth.get('subscriptions')) || List()
  return providerName && providerName !== PAYMENT_PROVIDER_NAME_ZUORA
    && standardSubscriptions.size > 0
}

export function getFmtvAccountType (creationSource) {
  let fmtvAccountType = ACCOUNT_TYPE_FMTV

  switch (creationSource) {
    case ACCOUNT_TYPE_FMTV_ITUNES:
      fmtvAccountType = ACCOUNT_TYPE_ITUNES
      break
    case ACCOUNT_TYPE_FMTV_ROKU:
      fmtvAccountType = ACCOUNT_TYPE_ROKU
      break
    case ACCOUNT_TYPE_FMTV_PAYPAL:
      fmtvAccountType = ACCOUNT_TYPE_PAYPAL_RECURRING
      break
    case ACCOUNT_TYPE_FMTV_ANDROID:
      fmtvAccountType = ACCOUNT_TYPE_ANDROID_PAY
      break
    case ACCOUNT_TYPE_FMTV_COMP:
      fmtvAccountType = ACCOUNT_TYPE_FMTV_COMP
      break
    case ACCOUNT_TYPE_FMTV_GIFT:
      fmtvAccountType = ACCOUNT_TYPE_FMTV_GIFT
      break
    default:
      fmtvAccountType = ACCOUNT_TYPE_FMTV
      break
  }

  return fmtvAccountType
}

/**
 * Get the Unifyd provider for a user (for granular feature flag control)
 * @param {Map} user - Immutable Map with user data
 * @returns {string|null} - Provider name (stripe, apple, google, amazon, roku, paypal) or null
 */
export function getUnifydProvider (user) {
  const creationSource = user.getIn(['data', 'creation_source'])
  const acquisitionSource = user.getIn(['data', 'acquisition_source'])

  // Prioritize creationSource, then acquisitionSource
  let source = _startsWith(creationSource, ACCOUNT_TYPE_UNIFYD, 0)
    ? creationSource
    : acquisitionSource

  if (source && source.includes('paypal')) {
    source = ACCOUNT_TYPE_UNIFYD_PAYPAL
  }

  return UNIFYD_PROVIDER_MAP[source] || null
}

/**
 * Check if user is a Unifyd migration user
 * @param {Map} user - Immutable Map with user data
 * @returns {boolean}
 */
export function isUnifydMigrationUser (user) {
  const creationSource = user.getIn(['data', 'creation_source'])
  const acquisitionSource = user.getIn(['data', 'acquisition_source'])
  const isCreationUnifyd = _startsWith(creationSource, ACCOUNT_TYPE_UNIFYD, 0)
  const isAcquisitionUnifyd = _startsWith(acquisitionSource, ACCOUNT_TYPE_UNIFYD, 0)

  return isCreationUnifyd || isAcquisitionUnifyd
}

/* eslint-disable consistent-return */
export function getUserAccountType (options) {
  const {
    user = Map(),
    userAccount = Map(),
    paytrack = Map(),
    auth = Map(),
  } = options
  const isLoggedIn = getAuthIsLoggedIn(auth)

  if (!auth || !userAccount || !paytrack || !user) {
    return
  }

  if (!Map.isMap(auth) || !Map.isMap(userAccount) || !Map.isMap(userAccount) || !Map.isMap(user)) {
    return
  }

  if (!isLoggedIn) {
    return
  }

  let accountType = ACCOUNT_TYPE_DEFAULT
  const billingAccount = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'billingAccountId'])
  const planCategory = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'planCategory'])
  const isAiOnly = planCategory === PLAN_CATEGORY_AI_ONLY
  const isPrimaryProfile = isPrimaryAccountProfile(options)
  const isFreeTrial = user.getIn(['data', 'freeTrialNoBillingInfo', 'ratePlanId'])
  const billingProvider = paytrack.getIn(['lastTransaction', 'provider_name'])
  const creationSource = user.getIn(['data', 'creation_source'])
  const acquisitionSource = user.getIn(['data', 'acquisition_source'])
  const isUnifydUser = isUnifydMigrationUser(user)
  const creationSourceFMTV = _startsWith(creationSource, ACCOUNT_TYPE_FMTV, 0)
  const creationSourceAmazon = _startsWith(creationSource, ACCOUNT_TYPE_AMAZON, 0)
  const creationSourceItunes = _startsWith(creationSource, ACCOUNT_TYPE_ITUNES, 0)
  const creationSourceRoku = _startsWith(creationSource, ACCOUNT_TYPE_ROKU, 0)
  const creationSourceGoogle = _startsWith(creationSource, ACCOUNT_TYPE_GOOGLE, 0)
  const creationSourceZuora = _startsWith(creationSource, ACCOUNT_TYPE_ZUORA, 0)
  const creationSourceUnifyd = _startsWith(creationSource, ACCOUNT_TYPE_UNIFYD, 0)
  const acquisitionSourceUnifyd = _startsWith(acquisitionSource, ACCOUNT_TYPE_UNIFYD, 0)

  // if the user has a free trial with no billing info
  if (!billingAccount && isFreeTrial) {
    accountType = ACCOUNT_TYPE_TRIAL

    return accountType
  }

  // if the user is not the primary profile
  if (!isPrimaryProfile) {
    accountType = ACCOUNT_TYPE_SECONDARY_PROFILE

    return accountType
  }

  // if this is an fmtv user
  if (!billingAccount && creationSourceFMTV) {
    accountType = getFmtvAccountType(creationSource)

    return accountType
  }

  // Unifyd Migration - Step 2 (Subscription Import Lockdown)
  if (isUnifydMigrationStepTwo && isUnifydUser) {
    const unifydProvider = getUnifydProvider(user)

    if (providerLockdownFlags[unifydProvider]) {
      return ACCOUNT_TYPE_UNIFYD_MIGRATION
    }
  }

  // Unifyd Migration - Step 1 (User Import Messaging)
  if (isUnifydMigrationStepOne && isUnifydUser) {
    const isCancelledUser = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'status']) === USER_ACCOUNT_CANCELLED
    const isNewUnifydUser = creationSourceUnifyd && acquisitionSourceUnifyd
    const isGaiaCancelledUser = isCancelledUser && !creationSourceUnifyd && acquisitionSourceUnifyd

    if (isNewUnifydUser || isGaiaCancelledUser) {
      return ACCOUNT_TYPE_UNIFYD_FREE_ACCESS
    }
  }

  // Unifyd Migration Complete - Step 3 (After migration)
  if (isUnifydUser) {
    const unifydProvider = getUnifydProvider(user)
    const hasEntitlements = auth.get('subscriptions') && auth.get('subscriptions').size > 0
    const hasBillingData = !!userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'billingAccountId'])

    // is paypal, has entitlements but not billing info
    if (unifydProvider === UNIFYD_PROVIDER_PAYPAL
      && hasEntitlements
      && !hasBillingData
    ) {
      return ACCOUNT_TYPE_UNIFYD_PAYPAL_COMP
    }

    return unifydProvider
  }

  // if this is a zuora account
  if (billingProvider === ACCOUNT_TYPE_ZUORA) {
    accountType = ACCOUNT_TYPE_ZUORA

    if (isAiOnly) {
      accountType = ACCOUNT_TYPE_AI_ONLY
    }

    return accountType
  }

  // if this is an itunes billing provider
  if (billingProvider === ACCOUNT_TYPE_ITUNES) {
    accountType = ACCOUNT_TYPE_ITUNES

    return accountType
  }

  // if this is an amazon billing provider
  if (billingProvider === ACCOUNT_TYPE_AMAZON) {
    accountType = ACCOUNT_TYPE_AMAZON

    return accountType
  }

  // if this is an roku billing provider
  if (billingProvider === ACCOUNT_TYPE_ROKU) {
    accountType = ACCOUNT_TYPE_ROKU

    return accountType
  }

  // if this is an google billing provider
  if (billingProvider === ACCOUNT_TYPE_GOOGLE) {
    accountType = ACCOUNT_TYPE_GOOGLE

    return accountType
  }

  // if billing provider has not helped to identify the account,
  // look at the creation source
  if (creationSourceZuora) {
    accountType = ACCOUNT_TYPE_ZUORA

    if (isAiOnly) {
      accountType = ACCOUNT_TYPE_AI_ONLY
    }

    return accountType
  }

  if (creationSourceAmazon) {
    accountType = ACCOUNT_TYPE_AMAZON

    return accountType
  }

  if (creationSourceItunes) {
    accountType = ACCOUNT_TYPE_ITUNES

    return accountType
  }

  if (creationSourceRoku) {
    accountType = ACCOUNT_TYPE_ROKU

    return accountType
  }

  if (creationSourceGoogle) {
    accountType = ACCOUNT_TYPE_GOOGLE

    return accountType
  }

  // if they have a billing provider but have no billing account
  // and none of the above criteria have been met,
  // just set the account type to the value of the provider
  if (billingProvider && !billingAccount) {
    accountType = billingProvider

    return accountType
  }

  return accountType
}
/* eslint-enable consistent-return */
